import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import StripeLoading from './components/StripeLoading';
import { registerStripeLoading } from './utils/stripe';
import Discover from './pages/Discover';
import Explore from './pages/Explore';
import Library from './pages/Library';
import BookReader from './pages/BookReader';
import BookDetail from './pages/BookDetail';
import AuthorDashboard from './pages/AuthorDashboard';
import WriteBook from './pages/WriteBook';
import WriteChapter from './pages/WriteChapter';
import Profile from './pages/Profile';
import Trending from './pages/Trending';
import ReadingLists from './pages/ReadingLists';
import Messages from './pages/Messages';
import ErrorBoundary from './components/ErrorBoundary';
import Feed from './pages/Feed';
import Landing from './pages/Landing';
import AdBlockDetector from './components/AdBlockDetector';
import Leaderboard from './pages/Leaderboard';
import About from './pages/About';
import Contest from './pages/Contest';
import Shop from './pages/Shop';
import NotFound from './pages/NotFound';
import Logo from './components/Logo';
import EasterEgg from './components/EasterEgg';
import AuthorProfile from './pages/AuthorProfile';
import BetaReport from './pages/BetaReport';
import Navbar from './components/Navbar';
import { UserProvider, useUser } from './context/UserContext';
import { LibraryProvider } from './context/LibraryContext';
import { BooksProvider } from './context/BooksContext';
import { SocialProvider } from './context/SocialContext';
import AuthAction from './pages/AuthAction';
import { ReadingListProvider } from './context/ReadingListContext';
import { ThemeProvider } from './context/ThemeContext';
import './App.css';

const Auth = lazy(() => import('./pages/Auth'));
const Legal = lazy(() => import('./pages/Legal'));

const PAYMENT_RESULT = new URL(window.location.href).searchParams.get('payment');
if (PAYMENT_RESULT) {
  sessionStorage.setItem('plumly_payment', PAYMENT_RESULT);
  const cleanUrl = new URL(window.location.href);
  cleanUrl.searchParams.delete('payment');
  window.history.replaceState({}, '', cleanUrl.pathname + cleanUrl.search);
}

// Message "connexion requise" pour les pages privées
function RequireAuth({ children, label }) {
  const { user } = useUser();
  const navigate = React.useCallback(() => {}, []);
  if (user) return children;
  return (
    <div className="auth-required">
      <div className="auth-required-content">
        <div className="auth-required-icon">🔒</div>
        <h2 className="auth-required-title">Connexion requise</h2>
        <p className="auth-required-text">
          Connecte-toi ou crée un compte pour accéder à {label || 'cette fonctionnalité'}.
        </p>
        <div className="auth-required-actions">
          <a href="/auth" className="btn btn-primary auth-required-btn">Se connecter / S'inscrire</a>
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const { user, loading } = useUser();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

useEffect(() => {
  registerStripeLoading(setStripeLoading);
}, []);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const mDoc = await getDoc(doc(db, 'config', 'maintenance'));
        if (mDoc.exists() && mDoc.data().active) {
          setMaintenanceMode(true);
        }
      } catch (e) {}
      setMaintenanceChecked(true);
    };
    checkMaintenance();
  }, []);

  if (loading || !maintenanceChecked) {
    return (
      <div className="loading-screen">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="loading-content"
        >
          <Logo size={64} animated={true} />
          <h1 className="loading-logo">Plumly</h1>
          <div className="loading-dots">
            <motion.span animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: 0 }} className="loading-dot" />
            <motion.span animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: 0.2 }} className="loading-dot" />
            <motion.span animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: 0.4 }} className="loading-dot" />
          </div>
        </motion.div>
      </div>
    );
  }

  const isPrivileged = user && ['admin', 'superadmin', 'moderator', 'beta'].includes(user.accountType);

  if (maintenanceMode && user && !isPrivileged) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        textAlign: 'center',
        fontFamily: 'var(--font-body)',
      }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🔧</div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, marginBottom: 8 }}>Plumly en maintenance</h1>
        <p style={{ fontSize: 15, opacity: 0.5, maxWidth: 300, lineHeight: 1.6 }}>
          La plateforme est en cours de maintenance. Reviens très vite pour découvrir le Tinder des livres !
        </p>
      </div>
    );
  }

if (user && user.pendingVerification && !window.location.pathname.startsWith('/verify-email')) {
  return (
    <Suspense fallback={<div style={{ textAlign: 'center', paddingTop: 60, opacity: 0.3 }}>Chargement...</div>}>
      <Auth />
    </Suspense>
  );
}

  return (
    <div className="app">
      <StripeLoading visible={stripeLoading} />
      {user && <EasterEgg />}
      <AdBlockDetector />
      <div className="app-content">
        <Suspense fallback={<div style={{ textAlign: 'center', paddingTop: 60, opacity: 0.3 }}>Chargement...</div>}>
          <Routes>
            {/* === PAGES PUBLIQUES === */}
            <Route path="/" element={<Landing />} />
            <Route path="/swipe" element={<Discover />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/trending" element={<Trending />} />
            <Route path="/book/:bookId" element={<BookDetail />} />
            <Route path="/read/:bookId" element={<BookReader />} />
            <Route path="/feed" element={<Feed />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/contest" element={<Contest />} />
            <Route path="/author/:authorId" element={<AuthorProfile />} />
            <Route path="/about" element={<About />} />
            <Route path="/legal/:page" element={<Legal />} />
            <Route path="/auth" element={user && !user.pendingVerification ? <Navigate to="/swipe" replace /> : <Auth />} />
            <Route path="/verify-email" element={<AuthAction />} />

            {/* === PAGES PRIVÉES (message connexion requise) === */}
            <Route path="/library" element={<RequireAuth label="ta bibliothèque"><Library /></RequireAuth>} />
            <Route path="/lists" element={<RequireAuth label="tes listes de lecture"><ReadingLists /></RequireAuth>} />
            <Route path="/messages" element={<RequireAuth label="la messagerie"><Messages /></RequireAuth>} />
            <Route path="/messages/:convId" element={<RequireAuth label="la messagerie"><Messages /></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth label="ton profil"><Profile /></RequireAuth>} />
            <Route path="/shop" element={<RequireAuth label="la boutique"><Shop /></RequireAuth>} />
            <Route path="/author" element={<RequireAuth label="tes œuvres"><AuthorDashboard /></RequireAuth>} />
            <Route path="/author/write" element={<RequireAuth label="l'éditeur"><WriteBook /></RequireAuth>} />
            <Route path="/author/write/:bookId/chapter" element={<RequireAuth label="l'éditeur"><WriteChapter /></RequireAuth>} />
            <Route path="/beta-report" element={<RequireAuth label="le beta report"><BetaReport /></RequireAuth>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
      <Navbar />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <UserProvider>
            <BooksProvider>
              <LibraryProvider>
                <SocialProvider>
                  <ReadingListProvider>
                    <AppContent />
                  </ReadingListProvider>
                </SocialProvider>
              </LibraryProvider>
            </BooksProvider>
          </UserProvider>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;